import { useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { TdsFileInstance } from '@trv-tds/core/dist/js/modules/file-upload/TdsFile'
import { TdsFileupload } from '@trv-tds/react'
import { compose } from 'redux'

import type { GlobalState } from '../types'
import {
  addError,
  attachFilesToState,
  clearError,
  uploadSelectCategory
} from './upload-actions'
import {
  EXCEEDED_MAX,
  EXCEEDED_MAX_QTY,
  MAX_QUANTITY,
  MAX_UPLOAD_SIZE,
  MISSING_CATEGORY
} from './upload-constants'
import { getFilesLength, getUploadButtonDisabledStatus, isS3Approved } from './upload-selectors'

const generateRandomNumberString = (value: number) => {
  // Random number string of length value
  const array = new Uint8Array(value)
  const rand = window.crypto.getRandomValues(array).join('')
  return rand.slice(0, value)
}

const greaterThanMax = (value: number) => value > MAX_UPLOAD_SIZE
const generateId = (name: string) =>
  `${name.replace(/\s/g,'').substring(1, 10)}${generateRandomNumberString(10)}`

type FileObjType = {
  id: string
  file: File
  category: string
  attachmentId: number
}

type ParsedFileObj = {
  files: FileObjType[]
  errors: { type: string, message: string }[]
}

type UploadButtonProps = {
  disabled: boolean
  onAddError(type: string, total: number | string): void
  category: string
  addFilesToState(files: FileObjType[]): void
  onSelectCategory(category: string): void
  fileLength: number
  id?: string
}

const UploadAreaComponent = (props: UploadButtonProps) => {
  const {
    disabled,
    fileLength,
    onAddError,
    category,
    addFilesToState,
    onSelectCategory
  } = props
  const fileInput = useRef<HTMLInputElement | null>(null)

  const parseFileObject = useCallback(
    (fileInstanceArray: TdsFileInstance[]) => fileInstanceArray.reduce<ParsedFileObj>(
      (acc, { file, id: attachmentId }) => {
        if (!category) {
          return {
            ...acc,
            errors: [
              ...acc.errors,
              {
                type: MISSING_CATEGORY,
                message:
                  'File attachments need a category to be uploaded. Please select a category.'
              }
            ]
          }
        }
        const id = generateId(file.name)
        if (isS3Approved(file)) {
          return {
            ...acc,
            files: [
              ...acc.files,
              {
                id,
                attachmentId,
                file,
                category
              }
            ]
          }
        } else if(greaterThanMax(file.size)) {
          return {
            ...acc,
            errors: [
              ...acc.errors,
              { type: EXCEEDED_MAX, message: `${file.name}` }
            ]
          }
        } else {
          return {
            ...acc,
            files: [
              ...acc.files,
              {
                id,
                file,
                attachmentId,
                category
              }
            ],
          }
        }
      },
      { files: [], errors: [] }
    ),
    [category]
  )


  const previewFiles = useCallback((fileArray: ParsedFileObj) => {
    addFilesToState(fileArray.files)
    return fileArray
  }, [addFilesToState])

  const fileErrors = useCallback((fileArray: ParsedFileObj) => {
    fileArray.errors.forEach(e => onAddError(e.type, e.message))
    return fileArray
  }, [onAddError])

  const clearCategory = useCallback((fileArray: ParsedFileObj) => {
    onSelectCategory('')
    return fileArray
  }, [onSelectCategory])

  const processFiles = compose(
    previewFiles,
    fileErrors,
    clearCategory
  )

  const validateQuantity = useCallback((fileObject: TdsFileInstance[] | null) => {
    if (!fileObject) return []
    const total = fileLength + fileObject.length
    if (total > MAX_QUANTITY) {
      onAddError(EXCEEDED_MAX_QTY, total)
      return []
    }
    return fileObject
  }, [onAddError, fileLength])

  const loadFiles = useCallback(
    (event: any) => {
      const validQuantity = validateQuantity(event.target.files)

      processFiles(
        parseFileObject(validQuantity)
      )
      if (!fileInput.current) return
      fileInput.current.value = ''
    },
    [validateQuantity, processFiles, parseFileObject, fileInput]
  )

  return (
    <TdsFileupload
      id='dragAndDropUpload'
      onTdsFileAdded={loadFiles}
      fileListAriaLive='off'
      showFilelist={false} // File list is handled with Preview
      allowDuplicates={true}
      maxCount={MAX_QUANTITY}
      aria-disabled={disabled}
      files={[]} // Files are handled in redux state and not needed here
    >
      <span slot='intro'>Select from the dropdown to upload files</span>
    </TdsFileupload>
  )
}

const mapStateToProps = (state: GlobalState) => ({
  fileLength: getFilesLength(state),
  disabled: getUploadButtonDisabledStatus(state),
  category: state.upload.category
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  addFilesToState: (files: FileObjType[]) => dispatch(attachFilesToState(files)),
  onAddError: (name: string, error: number | string) => dispatch(addError(name, error)),
  onClearError: (name: string) => dispatch(clearError(name)),
  onSelectCategory: (category: string) => dispatch(uploadSelectCategory(category)),
})

const UploadArea = connect(mapStateToProps, mapDispatchToProps)(UploadAreaComponent)

export { UploadArea }
